import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where,
  limit,
  orderBy,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { db } from "../firebase/firebase";

export interface InvoiceRecord {
  id: string;
  Type?: string; // Can be "invoice" or "guidance"
  type?: string; // Alternative casing
  Qrid: string; // Common identifier with QridRecord
  shortenLink: string; // URL for the PDF file
  Timestamp?: any;
  "Invoice No"?: string; // Exact field name with space as in the database
  Invoice_No?: string;
  Invoice_Record?: string;
  UniqueID?: string;
  contact_No?: string;
  Name?: string;
  OrderID?: string;
  Return_date?: string;
  [key: string]: any; // To allow for any other fields in the document
}

// New interface for RefundRecord
export interface RefundRecord {
  id: string;
  Qrid: string;
  Invoice_No?: string;
  Refund_Record?: string;
  shortenLink?: string;
  permanentLink?: string; // Secure link for admin use
  secureFilePath?: string;
  processed?: boolean;
  processedAt?: any;
  processError?: string;
  processErrorAt?: any;
  Type?: string;
  [key: string]: any; // To allow for any other fields in the document
}

// Memory cache to reduce Firestore reads
const documentCache: {
  [key: string]: {
    data: any,
    timestamp: number
  }
} = {};

// Cache validity duration in milliseconds
const CACHE_VALIDITY = 5 * 60 * 1000; // 5 minutes

// Helper to check if cache is valid
const isCacheValid = (cacheKey: string): boolean => {
  if (documentCache[cacheKey]) {
    const now = Date.now();
    return (now - documentCache[cacheKey].timestamp) < CACHE_VALIDITY;
  }
  return false;
};

// Helper to get data from cache or fallback to query
const getFromCacheOrQuery = async <T>(
  cacheKey: string,
  queryFn: () => Promise<T>
): Promise<T> => {
  // Check if we have valid cache
  if (isCacheValid(cacheKey)) {
    console.log(`Cache hit for key: ${cacheKey}`);
    return documentCache[cacheKey].data as T;
  }

  // Execute the query function
  const result = await queryFn();
  
  // Store in cache
  documentCache[cacheKey] = {
    data: result,
    timestamp: Date.now()
  };
  
  return result;
};

// Get all Invoice Records by Qrid - optimized version
export const getInvoiceRecordsByQrid = async (qrid: string) => {
  try {
    const cacheKey = `invoice_records_${qrid}`;
    return getFromCacheOrQuery(cacheKey, async () => {
      console.log(`Cache miss: Fetching InvoiceRecords for Qrid: ${qrid}`);
      
      // Trim the Qrid to handle any whitespace issues
      const trimmedQrid = qrid.trim();
      
      // Query Invoice Records with the matching Qrid - only fetch necessary fields
      const invoiceQuery = query(
        collection(db, "Invoice Record"),
        where("Qrid", "==", trimmedQrid),
        limit(50) // Add limit to prevent too many documents
      );
      
      const querySnapshot = await getDocs(invoiceQuery);
      console.log(`Found ${querySnapshot.size} InvoiceRecords with Qrid: ${trimmedQrid}`);
      
      // If no results, return empty array early
      if (querySnapshot.size === 0) {
        return [];
      }
      
      // Map the results to our interface
      const invoiceRecords: InvoiceRecord[] = [];
      querySnapshot.forEach((doc) => {
        invoiceRecords.push({
          id: doc.id,
          ...doc.data()
        } as InvoiceRecord);
      });
      
      return invoiceRecords;
    });
  } catch (error) {
    console.error("Error getting Invoice Records:", error);
    throw error;
  }
};

// Get Invoice Record by ID - optimized version
export const getInvoiceRecordById = async (id: string) => {
  try {
    const cacheKey = `invoice_record_${id}`;
    return getFromCacheOrQuery(cacheKey, async () => {
      const docRef = doc(db, "Invoice Record", id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as InvoiceRecord;
      } else {
        return null;
      }
    });
  } catch (error) {
    console.error("Error getting Invoice Record:", error);
    throw error;
  }
};

// Get all Invoice PDFs by Qrid and type - optimized version
export const getInvoicesByQridAndType = async (qrid: string, type: "invoice" | "guidance" = "invoice") => {
  try {
    const cacheKey = `invoices_by_type_${qrid}_${type}`;
    return getFromCacheOrQuery(cacheKey, async () => {
      console.log(`Cache miss: Fetching '${type}' documents with Qrid: ${qrid}`);
      
      // Trim the Qrid
      const trimmedQrid = qrid.trim();
      
      // Direct query with type field check to reduce processing
      const typeQuery = query(
        collection(db, "Invoice Record"),
        where("Qrid", "==", trimmedQrid),
        where("Type", "==", type),
        limit(10) // Limit to prevent too many documents
      );
      
      try {
        const querySnapshot = await getDocs(typeQuery);
        console.log(`Found ${querySnapshot.size} ${type} documents with direct type query`);
        
        const documents: InvoiceRecord[] = [];
        querySnapshot.forEach((doc) => {
          documents.push({
            id: doc.id,
            ...doc.data()
          } as InvoiceRecord);
        });
        
        if (documents.length > 0) {
          return documents;
        }
      } catch (e) {
        console.log("Error with direct type query, falling back to case-insensitive approach:", e);
      }
      
      // Fallback to the previous approach if direct query returns no results
      const invoiceQuery = query(
        collection(db, "Invoice Record"),
        where("Qrid", "==", trimmedQrid),
        limit(20)
      );
      
      const querySnapshot = await getDocs(invoiceQuery);
      console.log(`Found ${querySnapshot.size} total documents for fallback filtering`);
      
      // Filter by type in memory
      const documentsOfType = Array.from(querySnapshot.docs)
        .filter(doc => {
          const data = doc.data();
          const docType = data.Type || data.type || '';
          return docType.toLowerCase() === type.toLowerCase();
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as InvoiceRecord));
      
      console.log(`After filtering, found ${documentsOfType.length} ${type} documents`);
      return documentsOfType;
    });
  } catch (error) {
    console.error(`Error getting ${type} documents:`, error);
    throw error;
  }
};

// Get a URL - kept for compatibility but optimized
export const getInvoicePdfUrlByQrid = async (qrid: string, type: "invoice" | "guidance" = "invoice") => {
  try {
    const cacheKey = `invoice_url_${qrid}_${type}`;
    return getFromCacheOrQuery(cacheKey, async () => {
      const invoices = await getInvoicesByQridAndType(qrid, type);
      
      if (invoices.length > 0) {
        return invoices[0].shortenLink || null;
      }
      
      return null;
    });
  } catch (error) {
    console.error(`Error getting ${type} PDF URL:`, error);
    throw error;
  }
};

// Get refund records by Qrid - optimized version
export const getRefundRecordsByQrid = async (qrid: string): Promise<RefundRecord[]> => {
  try {
    const cacheKey = `refund_records_${qrid}`;
    return getFromCacheOrQuery(cacheKey, async () => {
      console.log(`Cache miss: Fetching RefundRecords with Qrid: ${qrid}`);
      
      // Trim the Qrid to handle any whitespace issues
      const trimmedQrid = qrid.trim();
      
      // Query RefundRecords with the matching Qrid
      const refundQuery = query(
        collection(db, "RefundRecord"),
        where("Qrid", "==", trimmedQrid),
        limit(10) // Add reasonable limit
      );
      
      const querySnapshot = await getDocs(refundQuery);
      console.log(`Found ${querySnapshot.size} RefundRecords with Qrid: ${trimmedQrid}`);
      
      const refundRecords: RefundRecord[] = [];
      
      querySnapshot.forEach((doc) => {
        refundRecords.push({
          id: doc.id,
          ...doc.data()
        } as RefundRecord);
      });
      
      return refundRecords;
    });
  } catch (error) {
    console.error("Error getting Refund Records:", error);
    throw error;
  }
}; 